import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import { toast } from "@/components/ui/toast";

/*
 * useGetAllContextPacks Query
 *
 * Query Key: ["contextPacks", params]
 */
export const useGetAllContextPacks = (params: api.GetAllContextPacksReq) => {
  return useQuery({
    queryKey: ["contextPacks", params],
    queryFn: () => api.getAllContextPacks(params),
  });
};

/*
 * useGetContextPack Query
 *
 * Query Key: ["contextPack", contextPackId]
 */
export const useGetContextPack = (contextPackId: string) => {
  return useQuery({
    queryKey: ["contextPack", contextPackId],
    queryFn: () => api.getContextPack(contextPackId),
    select: (data) => data.pack,
  });
};

/*
 * useUpdateContextPack Mutation
 *
 * Mutation Key: ["contextPacks"]
 */
export const useUpdateContextPack = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contextPackId, values }: { contextPackId: string; values: api.UpdateContextPackReq }) => api.updateContextPack(contextPackId, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contextPacks"] });
      qc.invalidateQueries({ queryKey: ["contextPack"] });
      toast({
        title: "New context pack published successfully",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update context pack",
        type: "error",
      });
    },
  });
};

/*
 * useArchiveContextPack Mutation
 *
 * Mutation Key: ["contextPacks", vars]
 */
export const useArchiveContextPack = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contextPackId, values }: { contextPackId: string; values: api.UpdateContextPackReq }) => api.updateContextPack(contextPackId, values),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["contextPacks"] });
      qc.invalidateQueries({ queryKey: ["contextPack"] });
      toast({
        title: `Context pack ${vars.values.isArchived ? "archived" : "unarchived"} successfully`,
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to archive context pack",
        type: "error",
      });
    },
  });
};

/*
 * useDeleteContextPack Mutation
 *
 * Mutation Key: ["contextPacks", vars]
 */
export const useDeleteContextPack = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteContextPack,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contextPacks"] });
      qc.invalidateQueries({ queryKey: ["contextPack"] });
      toast({
        title: "Context pack deleted successfully",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to delete context pack",
        type: "error",
      });
    },
  });
};

/*
 * useCreateContextPack Mutation
 *
 * Mutation Key: ["contextPacks", vars]
 */
export const useCreateContextPack = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createContextPack,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["contextPacks", vars] });
      qc.invalidateQueries({ queryKey: ["contextPack", _.pack.id] });
    },
    onError: () => {
      toast({
        title: "Failed to create context pack",
        type: "error",
      });
    },
  });
};

/*
 * useUpdateContext Mutation
 *
 * Mutation Key: ["contextPack", contextPackId]
 */
export const useUpdateContext = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contextId, values }: { contextId: string; contextPackId: string; values: api.UpdateContextReq }) => api.updateContext(contextId, values),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["contextPack", vars.contextPackId] });
    },
    onError: () => {
      toast({
        title: "Failed to update context pack",
        type: "error",
      });
    },
  });
};
